/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */

import React from 'react';

import Input from '@ntuctech/devex-tangram/Input';
import Box from '@ntuctech/devex-tangram/Box';

import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';
import TooltipText from '@ntuctech/devex-tangram/Tooltip/TooltipText';
import ButtonIcon from '@ntuctech/devex-tangram/Button/ButtonIcon';
import SvgInfo from '@ntuctech/devex-tangram/Icon/SvgInfo';

import { useAuth0 } from '../../Auth0Provider';
import FormWrapper from '../../theme/FormWrapper';
import FormElement from '../../theme/FormElement';
import FormActions from '../../theme/FormActions';
import FormFooter from '../../theme/FormFooter';
import TnC from '../../theme/TnC';
import ErrorLink from '../../theme/ErrorLink';
import { ConsentCheckbox, GroupConsent } from '../../theme/GroupConsent';

const gaScreenName = 'SignUp - Phone';

const EmailErrorWithLink = ({ initHandleMoveToEmailLogin }) => (
  <span>
    <Box displayBox="inline" mr={0.5}>
      You already have an account with us.
    </Box>
    <ErrorLink
      href="#"
      onClick={initHandleMoveToEmailLogin}
      size="s"
      data-testid="inputError-login"
    >
      Log in
    </ErrorLink>
    ?
  </span>
);

const PhoneSignUpForm = (props) => {
  const { trackPwl } = useAuth0();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    status,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
    handleMoveToEmailLogin,
  } = props;

  const [inputTooltipPlacement, setInputTooltipPlacement] = React.useState('top');

  const gaRef = React.useRef({});

  const { name, email, marketingConsentPreference } = values;

  const showErrorFor = (fieldName) => touched[fieldName] && errors[fieldName];

  const nameError = showErrorFor('name');
  let emailError = showErrorFor('email');

  const initHandleMoveToEmailLogin = () => {
    handleMoveToEmailLogin(email);
  };

  if (status && status.emailAvailable && status.emailAvailable[email] === false) {
    emailError = <EmailErrorWithLink initHandleMoveToEmailLogin={initHandleMoveToEmailLogin} />;
  }

  const isSubmitButtonDisabled = !dirty || !isValid || Boolean(emailError);

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled],
  );

  React.useEffect(() => {
    const matchMediaInstance = window.matchMedia('(max-width: 600px)');
    setInputTooltipPlacement(matchMediaInstance.matches ? 'top-end' : 'top');

    const handleResize = () => {
      setInputTooltipPlacement(matchMediaInstance.matches ? 'top-end' : 'top');
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleNameChange = (e) => {
    if (!gaRef.current.nameTypeTracked) {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Fill In',
        event_label: 'SignUp_Phone_Initiated',
      });
    }
    gaRef.current.nameTypeTracked = true;

    handleChange(e);
  };

  return (
    <FormWrapper>
      <FormElement>
        <Input
          id="signUpInputName"
          data-testid="signUpInputName"
          type="text"
          name="name"
          label="Name"
          value={name}
          helpText={nameError}
          error={Boolean(nameError)}
          onChange={handleNameChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="name"
        />
      </FormElement>
      <FormElement>
        <Input
          id="signUpInputHiddenEmail"
          data-testid="signUpInputHiddenEmail"
          type="email"
          name="email"
          label="Email"
          value={email}
          helpText={emailError}
          inputIcon={
            <TooltipText
              title="We'll send all the important communications to this email"
              placement={inputTooltipPlacement}
            >
              <ButtonIcon color="grey" type="button" data-testid="infoTooltipEmail">
                <SvgInfo />
              </ButtonIcon>
            </TooltipText>
          }
          error={Boolean(emailError)}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="username"
        />
      </FormElement>
      <FormElement spacing={{ mt: 3, mb: 2.5 }}>
        <ConsentCheckbox
          data-testid="signUpCheckboxOptIn"
          name="marketingConsentPreference"
          label={<GroupConsent />}
          checked={marketingConsentPreference}
          onChange={handleChange}
        />
      </FormElement>
      <FormActions spacing={{ mb: 3 }}>
        <ButtonContained
          data-testid="signUpButtonSubmit"
          fullWidth
          compact
          disabled={isSubmitButtonDisabled}
          loading={isSubmitting}
          onClick={submitForm}
        >
          Submit
        </ButtonContained>
      </FormActions>
      <FormFooter>
        <TnC type="signup" />
      </FormFooter>
    </FormWrapper>
  );
};

export default PhoneSignUpForm;
